import { startOfYear } from 'date-fns'

import { Z_INDEX } from 'theme/zIndex'

import { RATE_TYPE_OPTIONS, REMITTANCE_FREQUENCY_LABELS } from '../common/taxConstants'

/** The drawer stacks above the app bar (Z_INDEX.NAVIGATION), so its popups must sit one layer higher. */
export const DRAWER_POPPER_SX = { zIndex: Z_INDEX.MENU }

export interface TaxDrawerOption {
  value: string
  label: string
}

export interface TaxDrawerFormValues {
  name: string
  invoiceDisplayName: string
  taxZoneIds: string[]
  description: string
  rate: string | number
  rateType: TaxDrawerOption | null
  startDate: Date | string | null
  glAccountId: TaxDrawerOption | null
  remittanceFrequency: TaxDrawerOption | null
  invoiceGrouping: string
}

/** The slice of `transformTaxResponse` output the drawer reads when editing. */
export interface TaxDrawerTax {
  id: string
  name?: string | null
  invoiceDisplayName?: string | null
  taxZoneIds?: string[]
  description?: string | null
  rate?: number | null
  rateType?: string | null
  startDate?: string | null
  glAccountId?: string | null
  glAccountName?: string | null
  remittanceFrequency?: string | null
  invoiceGrouping?: string | null
}

// New taxes default to a Jan 1 start (taxes rarely change mid-year). Computed per
// open so the year is always current.
export const getCreateDefaultValues = (): TaxDrawerFormValues => ({
  name: '',
  invoiceDisplayName: '',
  taxZoneIds: [],
  description: '',
  rate: '',
  rateType: RATE_TYPE_OPTIONS[0],
  startDate: startOfYear(new Date()),
  glAccountId: null,
  remittanceFrequency: null,
  invoiceGrouping: 'consolidated',
})

/**
 * Seeds edit-mode values from a detail tax. Rate fields are seeded so the form is complete,
 * but the rate section is hidden in edit (rates change via the Rate adjustments tab) and
 * `buildUpdateTaxRequest` never reads them.
 */
export const buildDrawerValues = (tax: TaxDrawerTax): TaxDrawerFormValues => ({
  name: tax.name ?? '',
  invoiceDisplayName: tax.invoiceDisplayName ?? '',
  taxZoneIds: tax.taxZoneIds ?? [],
  description: tax.description ?? '',
  rate: tax.rate ?? '',
  rateType: RATE_TYPE_OPTIONS.find((option: TaxDrawerOption) => option.value === tax.rateType) ?? RATE_TYPE_OPTIONS[0],
  startDate: tax.startDate ?? null,
  glAccountId: tax.glAccountId ? { value: tax.glAccountId, label: tax.glAccountName ?? tax.glAccountId } : null,
  remittanceFrequency: tax.remittanceFrequency
    ? {
        value: tax.remittanceFrequency,
        label: (REMITTANCE_FREQUENCY_LABELS as Record<string, string>)[tax.remittanceFrequency] ?? tax.remittanceFrequency,
      }
    : null,
  invoiceGrouping: tax.invoiceGrouping ?? 'consolidated',
})
